import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Autocomplete,
  Switch,
  FormControlLabel,
  CircularProgress,
  Chip,
  Stack,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import { referenceAPI, geocodeAPI } from '../services/api';
import { actionsKabobCellSx, actionsKabobHeaderSx } from '../theme/tableStyles';
import ConfirmDialog from '../components/ConfirmDialog';
import RowActionsMenu from '../components/RowActionsMenu';
import {
  tablePageRootSx,
  tablePageHeaderStickySx,
  tablePageScrollRegionSx,
  tablePageTableContainerSx,
} from '../theme/tablePageLayout';
import AddExpenseLocationModal from '../components/AddExpenseLocationModal';
import { toArray } from '../utils/apiData';
import { createPrefixFilter } from '../utils/autocompleteFilters';
import { useNotify } from '../context/NotifyContext';
import errorMessage from '../utils/errorMessage';
import useFieldErrors from '../utils/useFieldErrors';
import { TableSkeletonRows } from '../components/LoadingSkeletons';

const sortLocations = (items = []) =>
  [...items].sort((a, b) => {
    if ((a.sort_order || 0) !== (b.sort_order || 0)) {
      return (a.sort_order || 0) - (b.sort_order || 0);
    }
    if ((a.name || '').localeCompare(b.name || '') !== 0) {
      return (a.name || '').localeCompare(b.name || '');
    }
    return (a.location || '').localeCompare(b.location || '');
  });

const AdminExpenseLocationsPage = () => {
  const notify = useNotify();
  const fields = useFieldErrors();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [locations, setLocations] = useState([]);
  const [locationNames, setLocationNames] = useState([]);
  const [openAddModal, setOpenAddModal] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [formData, setFormData] = useState({
    name_id: '',
    location: '',
    coordinates: '',
    online: false,
    active: true,
  });
  const [geocoding, setGeocoding] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [locationsRes, namesRes] = await Promise.all([
        referenceAPI.getExpenseLocations(),
        referenceAPI.getExpenseLocationNames(),
      ]);
      setLocations(sortLocations(toArray(locationsRes.data, 'expense locations')));
      setLocationNames(sortLocations(toArray(namesRes.data, 'expense location names')));
    } catch (err) {
      console.error('Error loading expense locations:', err);
      notify.error(errorMessage(err, 'Failed to load expense locations'));
    } finally {
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleOpenEdit = (location) => {
    fields.reset();
    const coordinateString =
      location.latitude && location.longitude
        ? `${location.latitude}, ${location.longitude}`
        : '';
    setSelectedLocation(location);
    setFormData({
      name_id: location.name_id ? String(location.name_id) : '',
      location: location.location || '',
      coordinates: coordinateString,
      online: Boolean(location.online),
      active: Boolean(location.active),
    });
    setEditDialogOpen(true);
  };

  const handleCloseEdit = () => {
    setSelectedLocation(null);
    setFormData({
      name_id: '',
      location: '',
      coordinates: '',
      online: false,
      active: true,
    });
    setGeocoding(false);
    setEditDialogOpen(false);
  };

  const lookupCoordinates = async () => {
    const selectedName = locationNames.find(n => String(n.id) === String(formData.name_id));
    const queryParts = [selectedName?.name, formData.location].filter(Boolean);
    if (!queryParts.length) return;

    setGeocoding(true);
    try {
      const { data } = await geocodeAPI.lookup(queryParts.join(', '));
      setFormData(prev => ({ ...prev, coordinates: `${data.lat}, ${data.lng}` }));
    } catch (err) {
      notify.error(errorMessage(err, 'Could not reach the geocoding service.'));
    } finally {
      setGeocoding(false);
    }
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleEditSubmit = async () => {
    if (!selectedLocation) return;
    if (!fields.validate({ name_id: !formData.name_id && 'Location name is required' })) return;

    setSaving(true);
    try {
      const payload = {
        name_id: Number(formData.name_id),
        online: formData.online,
        active: Boolean(formData.active),
      };

      if (formData.online) {
        payload.location = null;
        payload.coordinates = '';
      } else {
        if (formData.location) {
          payload.location = formData.location;
        }
        payload.coordinates = formData.coordinates?.trim() || '';
      }

      await referenceAPI.updateExpenseLocation(selectedLocation.id, payload);
      handleCloseEdit();
      loadData();
    } catch (err) {
      console.error('Error updating expense location:', err);
      notify.error(errorMessage(err, 'Failed to update expense location'));
    } finally {
      setSaving(false);
    }
  };

  const [deleteTarget, setDeleteTarget] = useState(null);

  const handleDeleteClick = (location) => setDeleteTarget(location);
  const handleDeleteCancel = () => setDeleteTarget(null);

  const handleDeleteConfirm = async () => {
    try {
      await referenceAPI.deleteExpenseLocation(deleteTarget.id);
      setDeleteTarget(null);
      loadData();
    } catch (err) {
      console.error('Error deleting expense location:', err);
      notify.error(errorMessage(err, 'Failed to deactivate expense location'));
    }
  };

  const handleToggleActive = async (location) => {
    try {
      await referenceAPI.updateExpenseLocation(location.id, {
        active: !location.active,
      });
      loadData();
    } catch (err) {
      console.error('Error toggling active state:', err);
      notify.error(errorMessage(err, 'Failed to update status'));
    }
  };

  const locationName = (id) =>
    locationNames.find((name) => name.id === id)?.name || 'Unknown';

  return (
    <Box sx={tablePageRootSx}>
      <Box sx={tablePageHeaderStickySx}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: { xs: 'flex-start', sm: 'space-between' },
            alignItems: 'center',
            gap: { xs: 1.5, sm: 0 },
            mb: 3,
          }}
        >
          <Typography variant="h4">Expense Locations</Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setOpenAddModal(true)}
            sx={{
              minWidth: { xs: 0, sm: 'auto' },
              width: { xs: 32, sm: 'auto' },
              height: { xs: 32, sm: 'auto' },
              px: { xs: 0, sm: 2 },
              '& .MuiButton-startIcon': { mr: { xs: 0, sm: 1 } },
            }}
          >
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
              Add Expense Location
            </Box>
          </Button>
        </Box>
      </Box>

      <Box sx={tablePageScrollRegionSx}>
      <TableContainer component={Paper} sx={tablePageTableContainerSx}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell align="center" sx={actionsKabobHeaderSx}>Actions</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Location</TableCell>
              <TableCell>Online</TableCell>
              <TableCell>Coordinates</TableCell>
              <TableCell align="center">Active</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && <TableSkeletonRows columns={6} />}
            {locations.map((location) => (
              <TableRow key={location.id}>
                <TableCell align="center" sx={actionsKabobCellSx}>
                  <RowActionsMenu
                    actions={[
                      { key: 'edit', label: 'Edit', icon: <EditIcon fontSize="small" />, onClick: () => handleOpenEdit(location) },
                      { key: 'delete', label: 'Delete', icon: <DeleteIcon fontSize="small" />, onClick: () => handleDeleteClick(location), color: 'error' },
                    ]}
                  />
                </TableCell>
                <TableCell>{locationName(location.name_id)}</TableCell>
                <TableCell>
                  {location.location || (location.online ? 'Online' : '-')}
                </TableCell>
                <TableCell>{location.online ? 'Yes' : 'No'}</TableCell>
                <TableCell>
                  {location.latitude && location.longitude
                    ? `${location.latitude}, ${location.longitude}`
                    : '-'}
                </TableCell>
                <TableCell align="center">
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Switch
                      checked={Boolean(location.active)}
                      onChange={() => handleToggleActive(location)}
                      color="primary"
                    />
                    {!location.active && (
                      <Chip label="Inactive" size="small" color="warning" />
                    )}
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
            {!loading && locations.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  No expense locations found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      </Box>

      <AddExpenseLocationModal
        open={openAddModal}
        onClose={() => setOpenAddModal(false)}
        apiCall={referenceAPI.createExpenseLocation}
        onSuccess={() => {
          setOpenAddModal(false);
          loadData();
        }}
      />

      <Dialog
        open={editDialogOpen}
        onClose={handleCloseEdit}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Expense Location</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <Autocomplete
              size="small"
              fullWidth
              disableClearable
              options={locationNames}
              getOptionLabel={(name) => name.name}
              isOptionEqualToValue={(o, v) => String(o.id) === String(v?.id)}
              filterOptions={createPrefixFilter((name) => name.name)}
              value={locationNames.find((name) => String(name.id) === formData.name_id) ?? null}
              onChange={(e, newValue) => {
                fields.clear('name_id');
                setFormData((prev) => ({ ...prev, name_id: newValue ? String(newValue.id) : '' }));
              }}
              renderInput={(params) => <TextField {...params} label="Location Name" required {...fields.props('name_id')} />}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(formData.online)}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      online: e.target.checked,
                      // Clear location-specific data when switching to online
                      ...(e.target.checked
                        ? { location: '', coordinates: '' }
                        : {}),
                    }))
                  }
                />
              }
              label="Online Location"
            />

            {!formData.online && (
              <>
                <TextField
                  size="small"
                  name="location"
                  label="Location (City, State)"
                  value={formData.location}
                  onChange={handleEditChange}
                  fullWidth
                />
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                  <TextField
                    size="small"
                    name="coordinates"
                    label="Coordinates (Latitude, Longitude)"
                    value={formData.coordinates}
                    onChange={handleEditChange}
                    fullWidth
                    placeholder="e.g., 35.48086, -97.75584"
                    helperText="Format: latitude, longitude"
                  />
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={lookupCoordinates}
                    disabled={geocoding || (!formData.name_id && !formData.location)}
                    sx={{ minWidth: 'auto', px: 0.5, height: 40, flexShrink: 0 }}
                    title="Look up coordinates"
                  >
                    {geocoding ? <CircularProgress size={16} /> : <MyLocationIcon fontSize="small" />}
                  </Button>
                </Box>
              </>
            )}

            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(formData.active)}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      active: e.target.checked,
                    }))
                  }
                />
              }
              label="Active"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEdit}>Cancel</Button>
          <Button
            onClick={handleEditSubmit}
            variant="contained"
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={!!deleteTarget}
        title="Deactivate Expense Location"
        message={`Deactivate expense location "${deleteTarget?.name}"?`}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        confirmLabel="Deactivate"
        confirmColor="warning"
      />
    </Box>
  );
};

export default AdminExpenseLocationsPage;

